import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, FileText, Calendar, Clock, Mic } from 'lucide-react'
import { api } from '../lib/api'
import VisitPrepCard from '../components/patients/VisitPrepCard'
import { ClinicalTimeline } from '../components/patients/ClinicalTimeline'

interface Patient {
  id: string
  first_name: string
  last_name: string
  name: string  // Formatted as "Last, First"
  dob: string | null
  mrn: string | null
  allergies: string[]
  pmh: string[]
  medications: string[]
  ai_consent?: boolean | null
  ai_consent_source?: string | null
}

interface Encounter {
  id: string
  encounter_type: string
  procedure_name: string | null
  encounter_date: string
  status: string
  note_count: number
  note_status: string | null
  created_at: string
}

interface IntakeSession {
  id: string
  status: string
  completion_pct: number
  visit_reason: string | null
  summary: string | null
  created_at: string
  updated_at: string
}

const ENCOUNTER_TYPE_LABELS: Record<string, string> = {
  op_note: 'Operative Note',
  office: 'Office Visit',
  inpatient: 'Inpatient',
  consult: 'Consultation',
  procedure: 'Procedure',
  telehealth: 'Telehealth',
}

const STATUS_COLORS: Record<string, string> = {
  recording: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  empty: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  processing: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  draft: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  final: 'bg-green-500/20 text-green-400 border-green-500/30',
  error: 'bg-red-500/20 text-red-400 border-red-500/30',
}

export function PatientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  const [patient, setPatient] = useState<Patient | null>(null)
  const [encounters, setEncounters] = useState<Encounter[]>([])
  const [intakeSessions, setIntakeSessions] = useState<IntakeSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [capturing, setCapturing] = useState(false)

  useEffect(() => {
    if (id) {
      loadData()
    }
  }, [id])

  async function loadData() {
    try {
      setLoading(true)
      const [patientData, encountersData, intakeData] = await Promise.all([
        api.getPatient(id!),
        api.getPatientEncounters(id!),
        api.getPatientIntakeSessions(id!).catch(() => [] as any[]),
      ])
      setPatient(patientData)
      setEncounters(encountersData)
      setIntakeSessions(intakeData || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load patient data')
    } finally {
      setLoading(false)
    }
  }

  async function handleCapture() {
    if (capturing) return
    setCapturing(true)
    try {
      const enc = await api.createEncounter({
        encounter_type: null,
        capture_mode: 'ambient',
        patient_id: id,
      })
      navigate(`/encounter/${enc.id}?autoRecord=true`, { replace: true })
    } catch (err: any) {
      console.error('Failed to create encounter:', err)
      setCapturing(false)
    }
  }

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  function getStatusLabel(status: string): string {
    return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-brand-500 text-lg">Loading...</div>
      </div>
    )
  }

  if (error || !patient) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <button onClick={() => navigate('/patients')} className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4">
          <ArrowLeft size={18} />
          Back to Patients
        </button>
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-300 rounded-lg p-4">
          {error || 'Patient not found'}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <button 
        onClick={() => navigate('/patients')} 
        className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-6 transition-colors"
      >
        <ArrowLeft size={18} />
        Back to Patients
      </button>

      {/* Patient Info Card */}
      <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {patient.name}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-500 dark:text-gray-400">
              {patient.dob && <span>DOB: {patient.dob}</span>}
              {patient.mrn && <span>MRN: {patient.mrn}</span>}
              {/* AI documentation consent status */}
              {patient.ai_consent === true && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800" title={patient.ai_consent_source ? `Consent source: ${patient.ai_consent_source}` : undefined}>
                  ✓ AI consent on file
                </span>
              )}
              {patient.ai_consent === false && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-800" title={patient.ai_consent_source ? `Source: ${patient.ai_consent_source}` : undefined}>
                  ✕ AI documentation declined
                </span>
              )}
              {(patient.ai_consent === null || patient.ai_consent === undefined) && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                  AI consent: not recorded
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleCapture}
            disabled={capturing}
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Mic size={16} />
            {capturing ? 'Starting...' : 'Capture'}
          </button>
        </div>

        {/* Patient context */}
        {(patient.allergies.length > 0 || patient.pmh.length > 0 || patient.medications.length > 0) && (
          <div className="border-t border-gray-200 dark:border-gray-800 pt-4 space-y-3">
            {patient.allergies.length > 0 && (
              <div>
                <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">Allergies</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {patient.allergies.map((a, i) => (
                    <span key={i} className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-2 py-1 rounded border border-red-300 dark:border-red-800">
                      ⚠ {a}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {patient.pmh.length > 0 && (
              <div>
                <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">Past Medical History</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {patient.pmh.map((h, i) => (
                    <span key={i} className="text-xs bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-1 rounded border border-gray-200 dark:border-gray-700">
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {patient.medications.length > 0 && (
              <div>
                <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">Medications</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {patient.medications.map((m, i) => (
                    <span key={i} className="text-xs bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-1 rounded border border-gray-200 dark:border-gray-700">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Clinical Timeline */}
      {encounters.length > 0 && (
        <div className="mb-6">
          <ClinicalTimeline encounters={encounters} />
        </div>
      )}

      {/* Visit Prep */}
      <div className="mb-6">
        <VisitPrepCard patientId={id!} />
      </div>

      {/* Intake Forms */}
      {intakeSessions.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
            Intake Forms
            <span className="ml-2 text-sm font-normal text-gray-400 dark:text-gray-500">({intakeSessions.length} total)</span>
          </h2>
          <div className="space-y-3">
            {intakeSessions.map((intake) => {
              const statusBadge =
                intake.status === 'completed'
                  ? 'bg-green-500/20 text-green-400 border-green-500/30'
                  : intake.status === 'in_progress'
                  ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                  : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
              return (
                <div
                  key={intake.id}
                  onClick={() => navigate(`/intake?session=${intake.id}`)}
                  className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 hover:bg-gray-200 dark:hover:bg-gray-800/80 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${statusBadge}`}>
                        {intake.status === 'completed' ? 'Completed' : intake.status === 'in_progress' ? 'In Progress' : 'Pending'}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {Math.round(intake.completion_pct)}% complete
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      <Calendar size={12} className="inline mr-1" />
                      {formatDate(intake.created_at)}
                    </span>
                  </div>
                  {intake.visit_reason && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                      <span className="font-medium">Reason:</span> {intake.visit_reason}
                    </p>
                  )}
                  {intake.summary && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{intake.summary}</p>
                  )}
                  <p className="text-xs text-brand-500 mt-2 font-medium">View intake form →</p>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Encounters List */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
          Encounter History
          <span className="ml-2 text-sm font-normal text-gray-400 dark:text-gray-500">({encounters.length} total)</span>
        </h2>
      </div>

      {encounters.length === 0 ? (
        <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-8 text-center">
          <FileText size={48} className="mx-auto text-gray-500 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400 mb-4">No encounters yet for this patient</p>
          <button
            onClick={handleCapture}
            disabled={capturing}
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Mic size={16} />
            {capturing ? 'Starting...' : 'Capture'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {encounters.map((enc) => (
            <div
              key={enc.id}
              onClick={() => {
                // Navigate to note if one exists, otherwise to encounter
                if (enc.note_count > 0) {
                  navigate(`/encounter/${enc.id}/note`)
                } else {
                  navigate(`/encounter/${enc.id}`)
                }
              }}
              className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 hover:bg-gray-200 dark:hover:bg-gray-800/80 transition-colors cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                      {ENCOUNTER_TYPE_LABELS[enc.encounter_type] || enc.encounter_type}
                    </h3>
                    {enc.status !== 'empty' && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${STATUS_COLORS[enc.status] || STATUS_COLORS.draft}`}>
                      {getStatusLabel(enc.status)}
                    </span>
                    )}
                    {enc.note_status && enc.note_status !== enc.status && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${STATUS_COLORS[enc.note_status] || STATUS_COLORS.draft}`}>
                        Note: {getStatusLabel(enc.note_status)}
                      </span>
                    )}
                  </div>

                  {enc.procedure_name && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{enc.procedure_name}</p>
                  )}

                  <div className="flex items-center gap-4 text-xs text-gray-400 dark:text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(enc.encounter_date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {formatDate(enc.created_at)}
                    </span>
                    {enc.note_count > 0 && (
                      <span className="flex items-center gap-1">
                        <FileText size={12} />
                        {enc.note_count} note{enc.note_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
